import { useState, useEffect, useCallback, useRef } from 'react';

// ── useAsync ──────────────────────────────────────────────────────────────────
export const useAsync = (asyncFn, immediate = true) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError]     = useState(null);

  const execute = useCallback(async (...args) => {
    setLoading(true); setError(null);
    try {
      const result = await asyncFn(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Something went wrong');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [asyncFn]); // eslint-disable-line

  useEffect(() => { if (immediate) execute(); }, []); // eslint-disable-line

  return { data, loading, error, execute, setData };
};

// ── useDebounce ───────────────────────────────────────────────────────────────
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
};

// ── useLocalStorage ───────────────────────────────────────────────────────────
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch { return initialValue; }
  });

  const setValue = (value) => {
    setStoredValue(value);
    localStorage.setItem(key, JSON.stringify(value));
  };

  return [storedValue, setValue];
};

// ── useClickOutside ───────────────────────────────────────────────────────────
export const useClickOutside = (handler) => {
  const ref = useRef(null);
  useEffect(() => {
    const listener = (e) => { if (ref.current && !ref.current.contains(e.target)) handler(e); };
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [handler]);
  return ref;
};

// ── usePagination ─────────────────────────────────────────────────────────────
export const usePagination = (initialLimit = 20) => {
  const [page, setPage]   = useState(1);
  const [limit]           = useState(initialLimit);
  const [total, setTotal] = useState(0);

  const pages     = Math.ceil(total / limit);
  const hasNext   = page < pages;
  const hasPrev   = page > 1;
  const nextPage  = () => hasNext && setPage((p) => p + 1);
  const prevPage  = () => hasPrev && setPage((p) => p - 1);
  const goToPage  = (p) => setPage(Math.min(Math.max(1, p), pages));
  const reset     = () => setPage(1);

  return { page, limit, total, setTotal, pages, hasNext, hasPrev, nextPage, prevPage, goToPage, reset };
};

// ── useMediaQuery ─────────────────────────────────────────────────────────────
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const documentChangeHandler = () => setMatches(mediaQueryList.matches);
    
    // Set initial value
    setMatches(mediaQueryList.matches);

    // Listen for changes
    if (mediaQueryList.addListener) {
      mediaQueryList.addListener(documentChangeHandler);
    } else {
      mediaQueryList.addEventListener('change', documentChangeHandler);
    }
    
    return () => {
      if (mediaQueryList.removeListener) {
        mediaQueryList.removeListener(documentChangeHandler);
      } else {
        mediaQueryList.removeEventListener('change', documentChangeHandler);
      }
    };
  }, [query]);

  return matches;
};
